// // screens/CartScreen.jsx
// import React, {
//   useCallback,
//   useEffect,
//   useMemo,
//   useState,
// } from "react";
// import { Link } from "react-router-dom";
// import { useAuth } from "../../providers/AuthProvider";
// import { useTheme } from "../../hooks/useTheme";
// import { useToast } from "../../providers/ToastProvider";
// import {
//   ShoppingBag,
//   Minus,
//   Plus,
//   Trash2,
//   Truck,
//   Clock,
//   ShieldCheck,
//   CreditCard,
//   Wallet,
//   AlertCircle,
//   ArrowRight,
//   Package,
//   MapPin,
//   Layers,
//   Boxes,
// } from "lucide-react";
// import { useCart } from "../../contexts/CartContext";
// import { variantKeyLabel } from "../marketplace/pricingApi";
// import { isWholesale } from "../../utils/variants";

// const PAYMENT_METHODS = [
//   {
//     value: "PAYSTACK",
//     icon: CreditCard,
//     title: "Card or Mobile Money",
//     subtitle: "Secured by Paystack",
//   },
//   {
//     value: "WALLET",
//     icon: Wallet,
//     title: "Azoemart Wallet",
//     subtitle: "Instant checkout from balance",
//   },
// ];

// const ADDRESS_FIELDS = [
//   { key: "full_name", label: "Full name", placeholder: "Kwame Mensah", span: 2 },
//   { key: "phone", label: "Phone number", placeholder: "024 000 0000", type: "tel" },
//   { key: "city", label: "City / Town", placeholder: "Accra" },
//   { key: "region", label: "Region", placeholder: "Greater Accra" },
//   {
//     key: "address_line",
//     label: "Delivery address",
//     placeholder: "Hostel, block, room number",
//     span: 2,
//   },
// ];

// const money = (v) => `GH₵${Number(v || 0).toFixed(2)}`;

// const readError = (data, fallback = "Something went wrong") => {
//   if (!data) return fallback;
//   if (data.message) return data.message;
//   const errors = data.errors;
//   if (errors) {
//     const flat = Object.values(errors).flat().filter(Boolean);
//     if (flat.length) return flat.join(" ");
//   }
//   return fallback;
// };

// export default function CartScreen() {
//   const { authFetch } = useAuth();
//   const { colors } = useTheme();
//   const { showToast } = useToast();
//   const { refresh: refreshCart, clear: clearCart } = useCart();

//   const [cart, setCart] = useState(null);
//   const [loading, setLoading] = useState(true);
//   const [placing, setPlacing] = useState(false);
//   const [busyItems, setBusyItems] = useState(() => new Set());
//   const [paymentMethod, setPaymentMethod] = useState("PAYSTACK");
//   const [address, setAddress] = useState({
//     full_name: "",
//     phone: "",
//     address_line: "",
//     city: "",
//     region: "",
//   });
//   const [touched, setTouched] = useState({});

//   // ── Load cart ─────────────────────────────────────────────
//   const load = useCallback(async () => {
//     try {
//       const res = await authFetch("/marketplace/cart/");
//       const data = await res.json();
//       if (res.ok) setCart(data.data);
//       else showToast(readError(data, "Could not load cart"), "error");
//     } catch {
//       showToast("Network error loading cart", "error");
//     } finally {
//       setLoading(false);
//     }
//   }, [authFetch, showToast]);

//   useEffect(() => {
//     load();
//   }, [load]);

//   // ── Derived ───────────────────────────────────────────────
//   const items = cart?.items ?? [];
//   const isEmpty = !loading && items.length === 0;

//   const cartComposition = useMemo(() => {
//     const types = new Set(items.map((i) => i.item_type));
//     return {
//       hasReady: types.has("READY") || types.has("INSTANT"),
//       hasPreOrder: types.has("PRE_ORDER"),
//       isMixed: types.size > 1,
//     };
//   }, [items]);

//   /**
//    * Any wholesale item below its MOQ blocks checkout. The backend
//    * will reject the order anyway, but surfacing it here means the
//    * user sees the issue before they hit Pay.
//    */
//   const belowMoqItems = useMemo(
//     () =>
//       items.filter(
//         (i) => isWholesale(i) && Number(i.quantity) < Number(i.moq)
//       ),
//     [items]
//   );

//   /**
//    * Group items by product to compute per-product shipping fees.
//    * The backend charges a flat per-product fee (not per unit, not
//    * per variant). So summing once per distinct product.
//    */
//   const shippingBreakdown = useMemo(() => {
//     const byProduct = new Map();
//     items.forEach((i) => {
//       if (!i.product) return;
//       if (byProduct.has(i.product)) return;
//       byProduct.set(i.product, {
//         productId: i.product,
//         productName: i.product_name,
//         fee: Number(i.shipping_fee || 0),
//       });
//     });
//     const rows = [...byProduct.values()].filter((r) => r.fee > 0);
//     const total = rows.reduce((s, r) => s + r.fee, 0);
//     return { rows, total };
//   }, [items]);

//   const subtotal = useMemo(
//     () =>
//       items.reduce(
//         (sum, i) => sum + Number(i.price) * Number(i.quantity),
//         0
//       ),
//     [items]
//   );

//   const totalWithShipping = subtotal + shippingBreakdown.total;

//   const addressErrors = useMemo(() => {
//     const errs = {};
//     if (!address.full_name.trim()) errs.full_name = "Name is required";
//     if (!address.phone.trim()) errs.phone = "Phone is required";
//     else if (address.phone.replace(/\D/g, "").length < 9)
//       errs.phone = "Enter a valid phone number";
//     if (!address.address_line.trim()) errs.address_line = "Address is required";
//     if (!address.city.trim()) errs.city = "City is required";
//     return errs;
//   }, [address]);

//   const canCheckout =
//     !placing &&
//     items.length > 0 &&
//     !cartComposition.isMixed &&
//     belowMoqItems.length === 0 &&
//     Object.keys(addressErrors).length === 0;

//   // ── Item actions ──────────────────────────────────────────
//   const setItemBusy = useCallback((id, busy) => {
//     setBusyItems((prev) => {
//       const next = new Set(prev);
//       if (busy) next.add(id);
//       else next.delete(id);
//       return next;
//     });
//   }, []);

//   const recomputeCart = (nextItems) => {
//     if (!cart) return null;
//     const total = nextItems.reduce(
//       (sum, i) => sum + Number(i.price) * Number(i.quantity),
//       0
//     );
//     return {
//       ...cart,
//       items: nextItems,
//       total_price: total,
//       total_items: nextItems.reduce((s, i) => s + Number(i.quantity), 0),
//     };
//   };

//   const changeQuantity = async (item, nextQty) => {
//     if (busyItems.has(item.id)) return;

//     // Wholesale floor — refuse to go below MOQ and inform the buyer.
//     if (isWholesale(item) && nextQty < Number(item.moq)) {
//       showToast(
//         `Minimum order for ${item.product_name} is ${item.moq} units`,
//         "error"
//       );
//       return;
//     }
//     if (nextQty < 1) return;

//     const prevCart = cart;
//     const nextItems = cart.items.map((i) =>
//       i.id === item.id ? { ...i, quantity: nextQty } : i
//     );

//     setCart(recomputeCart(nextItems));
//     setItemBusy(item.id, true);

//     try {
//       const res = await authFetch(`/marketplace/cart/items/${item.id}/`, {
//         method: "PATCH",
//         body: JSON.stringify({ quantity: nextQty }),
//       });
//       const data = await res.json();

//       if (!res.ok) {
//         setCart(prevCart);
//         showToast(readError(data, "Could not update quantity"), "error");
//         return;
//       }

//       if (data.data?.items && Array.isArray(data.data.items)) {
//         setCart(data.data);
//       } else if (data.data?.quantity != null) {
//         setCart((c) => {
//           if (!c) return c;
//           const reconciled = c.items.map((i) =>
//             i.id === item.id ? { ...i, quantity: Number(data.data.quantity) } : i
//           );
//           return recomputeCart(reconciled);
//         });
//       }
//       refreshCart();
//     } catch {
//       setCart(prevCart);
//       showToast("Network error", "error");
//     } finally {
//       setItemBusy(item.id, false);
//     }
//   };

//   const removeItem = async (item) => {
//     if (busyItems.has(item.id)) return;

//     const prevCart = cart;
//     const nextItems = cart.items.filter((i) => i.id !== item.id);

//     setCart(recomputeCart(nextItems));
//     setItemBusy(item.id, true);

//     try {
//       const res = await authFetch(`/marketplace/cart/items/${item.id}/`, {
//         method: "DELETE",
//       });

//       if (!res.ok) {
//         setCart(prevCart);
//         const data = await res.json().catch(() => ({}));
//         showToast(readError(data, "Could not remove item"), "error");
//       } else {
//         refreshCart();
//       }
//     } catch {
//       setCart(prevCart);
//       showToast("Network error", "error");
//     } finally {
//       setItemBusy(item.id, false);
//     }
//   };

//   // ── Checkout ──────────────────────────────────────────────
//   const checkout = async () => {
//     setTouched(Object.fromEntries(Object.keys(address).map((k) => [k, true])));

//     if (Object.keys(addressErrors).length > 0) {
//       showToast("Please complete the delivery details", "error");
//       return;
//     }
//     if (cartComposition.isMixed) {
//       showToast(
//         "Ready-to-ship and pre-order items must be checked out separately",
//         "error"
//       );
//       return;
//     }
//     if (belowMoqItems.length > 0) {
//       const first = belowMoqItems[0];
//       showToast(
//         `Minimum order for ${first.product_name} is ${first.moq} units`,
//         "error"
//       );
//       return;
//     }

//     setPlacing(true);
//     try {
//       const orderRes = await authFetch("/marketplace/orders/", {
//         method: "POST",
//         body: JSON.stringify({
//           items: items.map((i) => ({
//             product_id: i.product,
//             quantity: i.quantity,
//             ...(i.variant_key && { variant_key: i.variant_key }),
//             ...(i.pre_order && { pre_order_id: i.pre_order }),
//           })),
//           shipping_address: address,
//           shipping_method: "standard",
//         }),
//       });
//       const orderData = await orderRes.json();
//       if (!orderRes.ok) {
//         throw new Error(readError(orderData, "Could not create order"));
//       }

//       const payRes = await authFetch(
//         `/marketplace/orders/${orderData.data.id}/pay/`,
//         {
//           method: "POST",
//           body: JSON.stringify({ payment_method: paymentMethod }),
//         }
//       );
//       const payData = await payRes.json();
//       if (!payRes.ok) {
//         throw new Error(readError(payData, "Payment could not be started"));
//       }

//       await authFetch("/marketplace/cart/clear/", { method: "DELETE" });
//       clearCart();
//       if (paymentMethod === "PAYSTACK" && payData.data?.authorization_url) {
//         window.location.assign(payData.data.authorization_url);
//         return;
//       }

//       showToast("Order paid from your wallet", "success");
//       await load();
//     } catch (e) {
//       showToast(e.message || "Checkout failed", "error");
//     } finally {
//       setPlacing(false);
//     }
//   };

//   // ── Render ────────────────────────────────────────────────
//   if (loading) {
//     return (
//       <div style={styles.loadingWrap(colors)}>
//         <div style={styles.spinner(colors)} />
//         <span style={{ fontSize: 13, color: colors.secondaryText }}>
//           Loading your cart…
//         </span>
//       </div>
//     );
//   }

//   if (isEmpty) {
//     return (
//       <div style={styles.pageWrap}>
//         <EmptyCart colors={colors} />
//       </div>
//     );
//   }

//   return (
//     <div style={styles.pageWrap}>
//       <header style={{ marginBottom: 24 }}>
//         <h1 style={styles.h1(colors)}>Your cart</h1>
//         <p style={styles.subtitle(colors)}>
//           {items.length} item{items.length !== 1 ? "s" : ""} ready for checkout
//         </p>
//       </header>

//       <div style={styles.layout} className="cart-layout-responsive">
//         {/* ── Left column ──────────────────────────────── */}
//         <div style={styles.leftCol}>
//           <Section
//             colors={colors}
//             title="Items"
//             icon={ShoppingBag}
//             count={items.length}
//           >
//             <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
//               {items.map((item) => (
//                 <CartRow
//                   key={item.id}
//                   item={item}
//                   colors={colors}
//                   busy={busyItems.has(item.id)}
//                   onChangeQty={(q) => changeQuantity(item, q)}
//                   onRemove={() => removeItem(item)}
//                 />
//               ))}
//             </div>

//             {cartComposition.isMixed && (
//               <InlineWarning colors={colors} tone="warning">
//                 Your cart mixes ready-to-ship and pre-order items. These must
//                 be checked out in separate orders. Remove one type to continue.
//               </InlineWarning>
//             )}

//             {belowMoqItems.length > 0 && (
//               <InlineWarning colors={colors} tone="error">
//                 {belowMoqItems.length === 1 ? (
//                   <>
//                     <strong>{belowMoqItems[0].product_name}</strong> requires a
//                     minimum order of {belowMoqItems[0].moq} units. Increase the
//                     quantity to continue.
//                   </>
//                 ) : (
//                   <>
//                     {belowMoqItems.length} wholesale items are below their
//                     minimum order. Increase each to its MOQ to continue.
//                   </>
//                 )}
//               </InlineWarning>
//             )}
//           </Section>

//           <Section colors={colors} title="Delivery details" icon={MapPin}>
//             <div style={styles.addressGrid} className="cart-address-grid">
//               {ADDRESS_FIELDS.map((f) => (
//                 <AddressField
//                   key={f.key}
//                   field={f}
//                   value={address[f.key]}
//                   error={touched[f.key] ? addressErrors[f.key] : null}
//                   onChange={(v) => setAddress((a) => ({ ...a, [f.key]: v }))}
//                   onBlur={() =>
//                     setTouched((t) => ({ ...t, [f.key]: true }))
//                   }
//                   colors={colors}
//                 />
//               ))}
//             </div>
//           </Section>
//         </div>

//         {/* ── Right column ─────────────────────────────── */}
//         <aside style={styles.rightCol}>
//           <div style={styles.summaryCard(colors)}>
//             <Section colors={colors} title="Payment" icon={CreditCard} bare>
//               <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
//                 {PAYMENT_METHODS.map((m) => {
//                   const Icon = m.icon;
//                   const active = paymentMethod === m.value;
//                   return (
//                     <button
//                       key={m.value}
//                       type="button"
//                       onClick={() => setPaymentMethod(m.value)}
//                       style={styles.paymentBtn(colors, active)}
//                     >
//                       <span style={styles.paymentIcon(colors, active)} aria-hidden>
//                         <Icon size={16} />
//                       </span>
//                       <span style={{ flex: 1, textAlign: "left" }}>
//                         <span
//                           style={{
//                             display: "block",
//                             fontSize: 13.5,
//                             fontWeight: 700,
//                             color: colors.heading,
//                           }}
//                         >
//                           {m.title}
//                         </span>
//                         <span
//                           style={{
//                             display: "block",
//                             fontSize: 11.5,
//                             color: colors.secondaryText,
//                             marginTop: 2,
//                           }}
//                         >
//                           {m.subtitle}
//                         </span>
//                       </span>
//                       <span style={styles.radio(colors, active)}>
//                         {active && <span style={styles.radioDot(colors)} />}
//                       </span>
//                     </button>
//                   );
//                 })}
//               </div>
//             </Section>

//             <div style={styles.divider(colors)} />

//             <Summary
//               colors={colors}
//               subtotal={subtotal}
//               shippingRows={shippingBreakdown.rows}
//               shippingTotal={shippingBreakdown.total}
//               total={totalWithShipping}
//             />

//             <button
//               type="button"
//               disabled={!canCheckout}
//               onClick={checkout}
//               style={styles.payBtn(colors, canCheckout)}
//             >
//               {placing ? (
//                 <>
//                   <span style={styles.spinnerInline(colors)} />
//                   Processing…
//                 </>
//               ) : (
//                 <>
//                   Place order · {money(totalWithShipping)}
//                   <ArrowRight size={16} />
//                 </>
//               )}
//             </button>

//             <div style={styles.reassure(colors)}>
//               <ShieldCheck size={13} />
//               Secure checkout · Your details are encrypted
//             </div>
//           </div>
//         </aside>
//       </div>
//     </div>
//   );
// }

// /* ══════════════════════════════════════════════════════════
//    Sub-components
//    ══════════════════════════════════════════════════════════ */

// const Section = ({ colors, title, icon: Icon, count, children, bare }) => (
//   <section style={{ marginBottom: bare ? 0 : 28 }}>
//     {title && (
//       <div
//         style={{
//           display: "flex",
//           alignItems: "center",
//           gap: 8,
//           marginBottom: 14,
//         }}
//       >
//         {Icon && (
//           <span
//             style={{
//               width: 28,
//               height: 28,
//               borderRadius: 8,
//               background: colors.subtleBg || colors.hoverBg,
//               color: colors.btnBg,
//               display: "inline-flex",
//               alignItems: "center",
//               justifyContent: "center",
//               flexShrink: 0,
//             }}
//           >
//             <Icon size={14} />
//           </span>
//         )}
//         <h2
//           style={{
//             margin: 0,
//             fontSize: 15,
//             fontWeight: 700,
//             color: colors.heading,
//             letterSpacing: "-0.01em",
//           }}
//         >
//           {title}
//           {typeof count === "number" && (
//             <span
//               style={{
//                 marginLeft: 8,
//                 fontSize: 12,
//                 fontWeight: 600,
//                 color: colors.secondaryText,
//               }}
//             >
//               ({count})
//             </span>
//           )}
//         </h2>
//       </div>
//     )}
//     {children}
//   </section>
// );

// const CartRow = ({ item, colors, busy, onChangeQty, onRemove }) => {
//   const isPreOrder = item.item_type === "PRE_ORDER";
//   const hasVariant = !!item.variant_key;
//   const wholesale = isWholesale(item);
//   const moqFloor = wholesale ? Number(item.moq) : 1;
//   const belowMoq = wholesale && Number(item.quantity) < moqFloor;

//   return (
//     <div
//       style={{
//         display: "flex",
//         gap: 14,
//         padding: 14,
//         borderRadius: 14,
//         border: `1px solid ${belowMoq ? colors.error : colors.border
//           }`,
//         background: colors.raisedBg,
//         opacity: busy ? 0.55 : 1,
//         transition: "opacity 0.15s ease, border-color 0.15s ease",
//         alignItems: "flex-start",
//         pointerEvents: busy ? "none" : "auto",
//       }}
//     >
//       <div
//         style={{
//           width: 76,
//           height: 76,
//           borderRadius: 10,
//           overflow: "hidden",
//           background: colors.subtleBg || colors.hoverBg,
//           flexShrink: 0,
//         }}
//       >
//         {item.product_image ? (
//           <img
//             src={item.product_image}
//             alt=""
//             style={{ width: "100%", height: "100%", objectFit: "cover" }}
//           />
//         ) : (
//           <div
//             style={{
//               width: "100%",
//               height: "100%",
//               display: "flex",
//               alignItems: "center",
//               justifyContent: "center",
//               color: colors.secondaryText,
//             }}
//           >
//             <Package size={22} />
//           </div>
//         )}
//       </div>

//       <div style={{ flex: 1, minWidth: 0 }}>
//         <div style={{ display: "flex", alignItems: "flex-start", gap: 8 }}>
//           <div style={{ flex: 1, minWidth: 0 }}>
//             <div
//               style={{
//                 fontSize: 14,
//                 fontWeight: 700,
//                 color: colors.heading,
//                 lineHeight: 1.35,
//                 display: "-webkit-box",
//                 WebkitLineClamp: 2,
//                 WebkitBoxOrient: "vertical",
//                 overflow: "hidden",
//               }}
//             >
//               {item.product_name}
//             </div>

//             {/* Variant chip */}
//             {hasVariant && (
//               <div
//                 style={{
//                   display: "inline-flex",
//                   alignItems: "center",
//                   gap: 4,
//                   marginTop: 4,
//                   padding: "2px 8px",
//                   borderRadius: 999,
//                   background: `${colors.btnBg}18`,
//                   color: colors.btnBg,
//                   fontSize: 10.5,
//                   fontWeight: 700,
//                   letterSpacing: 0.2,
//                 }}
//               >
//                 <Layers size={10} />
//                 {variantKeyLabel(item.variant_key)}
//               </div>
//             )}

//             <div
//               style={{
//                 marginTop: 6,
//                 display: "flex",
//                 alignItems: "center",
//                 gap: 6,
//                 flexWrap: "wrap",
//               }}
//             >
//               <span
//                 style={{
//                   display: "inline-flex",
//                   alignItems: "center",
//                   gap: 4,
//                   fontSize: 10.5,
//                   fontWeight: 700,
//                   letterSpacing: 0.3,
//                   textTransform: "uppercase",
//                   padding: "2px 8px",
//                   borderRadius: 999,
//                   color: isPreOrder ? colors.warning : colors.success,
//                   background: isPreOrder ? colors.warningBg : colors.successBg,
//                 }}
//               >
//                 {isPreOrder ? <Clock size={10} /> : <Truck size={10} />}
//                 {item.item_type_display ||
//                   (isPreOrder ? "Pre-order" : "Ready to ship")}
//               </span>

//               {/* Wholesale chip */}
//               {wholesale && (
//                 <span
//                   style={{
//                     display: "inline-flex",
//                     alignItems: "center",
//                     gap: 4,
//                     fontSize: 10.5,
//                     fontWeight: 700,
//                     letterSpacing: 0.3,
//                     textTransform: "uppercase",
//                     padding: "2px 8px",
//                     borderRadius: 999,
//                     background: `${colors.btnBg}18`,
//                     color: colors.btnBg,
//                   }}
//                 >
//                   <Boxes size={10} />
//                   Wholesale · Min {item.moq}
//                 </span>
//               )}

//               {/* Per-item shipping hint */}
//               {Number(item.shipping_fee || 0) > 0 && (
//                 <span
//                   style={{
//                     display: "inline-flex",
//                     alignItems: "center",
//                     gap: 4,
//                     fontSize: 10.5,
//                     color: colors.secondaryText,
//                     fontWeight: 600,
//                   }}
//                 >
//                   <Truck size={10} />
//                   +{money(item.shipping_fee)} shipping
//                 </span>
//               )}
//             </div>

//             {/* Below-MOQ inline error */}
//             {belowMoq && (
//               <div
//                 style={{
//                   marginTop: 8,
//                   display: "inline-flex",
//                   alignItems: "center",
//                   gap: 6,
//                   padding: "6px 10px",
//                   borderRadius: 8,
//                   background: colors.errorBg,
//                   color: colors.error,
//                   fontSize: 11.5,
//                   fontWeight: 600,
//                 }}
//               >
//                 <AlertCircle size={12} />
//                 Minimum is {moqFloor} units
//               </div>
//             )}
//           </div>

//           <button
//             type="button"
//             onClick={onRemove}
//             disabled={busy}
//             aria-label="Remove item"
//             style={styles.removeBtn(colors)}
//           >
//             <Trash2 size={15} />
//           </button>
//         </div>

//         <div
//           style={{
//             marginTop: 12,
//             display: "flex",
//             alignItems: "center",
//             justifyContent: "space-between",
//             gap: 10,
//             flexWrap: "wrap",
//           }}
//         >
//           <div style={styles.qtyGroup(colors)}>
//             <button
//               type="button"
//               onClick={() => onChangeQty(item.quantity - 1)}
//               disabled={busy || item.quantity <= moqFloor}
//               style={styles.qtyBtn(
//                 colors,
//                 busy || item.quantity <= moqFloor
//               )}
//               aria-label="Decrease quantity"
//             >
//               <Minus size={13} />
//             </button>
//             <span
//               style={{
//                 minWidth: 26,
//                 textAlign: "center",
//                 fontSize: 13.5,
//                 fontWeight: 700,
//                 color: colors.heading,
//               }}
//             >
//               {item.quantity}
//             </span>
//             <button
//               type="button"
//               onClick={() => onChangeQty(item.quantity + 1)}
//               disabled={busy}
//               style={styles.qtyBtn(colors, busy)}
//               aria-label="Increase quantity"
//             >
//               <Plus size={13} />
//             </button>
//           </div>

//           <div style={{ textAlign: "right" }}>
//             <div
//               style={{
//                 fontSize: 11,
//                 color: colors.secondaryText,
//                 fontWeight: 500,
//               }}
//             >
//               {money(item.price)} each
//             </div>
//             <div
//               style={{
//                 fontSize: 16,
//                 fontWeight: 800,
//                 color: colors.heading,
//                 letterSpacing: "-0.01em",
//               }}
//             >
//               {money(Number(item.price) * item.quantity)}
//             </div>
//           </div>
//         </div>
//       </div>
//     </div>
//   );
// };

// const AddressField = ({ field, value, error, onChange, onBlur, colors }) => (
//   <div style={{ gridColumn: `span ${field.span || 1}` }}>
//     <label
//       htmlFor={`addr-${field.key}`}
//       style={{
//         display: "block",
//         fontSize: 12,
//         fontWeight: 600,
//         color: colors.text,
//         marginBottom: 6,
//       }}
//     >
//       {field.label}
//     </label>
//     <input
//       id={`addr-${field.key}`}
//       type={field.type || "text"}
//       value={value}
//       placeholder={field.placeholder}
//       onChange={(e) => onChange(e.target.value)}
//       onBlur={onBlur}
//       style={{
//         width: "100%",
//         padding: "10px 12px",
//         borderRadius: 10,
//         border: `1px solid ${error ? colors.error : colors.border}`,
//         background: colors.raisedBg,
//         color: colors.text,
//         fontSize: 14,
//         outline: "none",
//         boxSizing: "border-box",
//         transition: "border-color 0.15s ease, box-shadow 0.15s ease",
//         boxShadow: error ? `0 0 0 3px ${colors.errorBg}` : "none",
//       }}
//     />
//     {error && (
//       <div
//         style={{
//           marginTop: 4,
//           fontSize: 11.5,
//           color: colors.error,
//           display: "flex",
//           alignItems: "center",
//           gap: 4,
//         }}
//       >
//         <AlertCircle size={11} />
//         {error}
//       </div>
//     )}
//   </div>
// );

// /* ══════════════════════════════════════════════════════════
//    Summary — subtotal + per-product shipping + total
//    ══════════════════════════════════════════════════════════ */

// const Summary = ({ colors, subtotal, total }) => (
//   <div>
//     <div style={styles.summaryRow(colors)}>
//       <span>Subtotal</span>
//       <span>{money(subtotal)}</span>
//     </div>

//     <div style={styles.shippingNote(colors)}>
//       <Truck size={13} />
//       <span>
//         Shipping is charged per item when it arrives in Ghana. You'll be
//         notified when it's ready to pay.
//       </span>
//     </div>

//     <div style={styles.summaryTotal(colors)}>
//       <span>Total now</span>
//       <span>{money(total)}</span>
//     </div>
//   </div>
// );

// const InlineWarning = ({ children, colors, tone = "warning" }) => {
//   const isError = tone === "error";
//   const fg = isError ? colors.error : colors.warning;
//   const bg = isError ? colors.errorBg : colors.warningBg;
//   return (
//     <div
//       style={{
//         marginTop: 12,
//         padding: 12,
//         borderRadius: 12,
//         background: bg,
//         border: `1px solid ${fg}33`,
//         color: fg,
//         fontSize: 12.5,
//         fontWeight: 600,
//         display: "flex",
//         alignItems: "flex-start",
//         gap: 8,
//         lineHeight: 1.5,
//       }}
//     >
//       <AlertCircle size={15} style={{ flexShrink: 0, marginTop: 1 }} />
//       <span>{children}</span>
//     </div>
//   );
// };

// const EmptyCart = ({ colors }) => (
//   <div
//     style={{
//       maxWidth: 400,
//       margin: "80px auto 0",
//       textAlign: "center",
//       padding: 24,
//     }}
//   >
//     <div
//       style={{
//         width: 76,
//         height: 76,
//         borderRadius: 22,
//         background: colors.subtleBg || colors.hoverBg,
//         color: colors.btnBg,
//         display: "inline-flex",
//         alignItems: "center",
//         justifyContent: "center",
//         marginBottom: 18,
//       }}
//     >
//       <ShoppingBag size={32} />
//     </div>
//     <h2
//       style={{
//         margin: "0 0 6px",
//         fontSize: 20,
//         fontWeight: 800,
//         color: colors.heading,
//         letterSpacing: "-0.02em",
//       }}
//     >
//       Your cart is empty
//     </h2>
//     <p
//       style={{
//         margin: "0 0 24px",
//         fontSize: 14,
//         color: colors.secondaryText,
//         lineHeight: 1.55,
//       }}
//     >
//       Browse data bundles or marketplace products to get started.
//     </p>
//     <div
//       style={{
//         display: "flex",
//         gap: 10,
//         justifyContent: "center",
//         flexWrap: "wrap",
//       }}
//     >
//       <Link to="/marketplace" style={styles.emptyCta(colors, true)}>
//         <ShoppingBag size={15} />
//         Browse market
//       </Link>
//       <Link to="/data" style={styles.emptyCta(colors, false)}>
//         <ArrowRight size={15} />
//         Buy data
//       </Link>
//     </div>
//   </div>
// );

// /* ══════════════════════════════════════════════════════════
//    Styles
//    ══════════════════════════════════════════════════════════ */

// const styles = {
//   pageWrap: {
//     maxWidth: 1080,
//     margin: "0 auto",
//     padding: "20px 16px 60px",
//   },
//   layout: {
//     display: "grid",
//     gridTemplateColumns: "minmax(0, 1fr)",
//     gap: 24,
//   },
//   leftCol: { minWidth: 0 },
//   rightCol: { minWidth: 0 },
//   h1: (c) => ({
//     margin: 0,
//     fontSize: 26,
//     fontWeight: 800,
//     letterSpacing: "-0.02em",
//     color: c.heading,
//   }),
//   subtitle: (c) => ({
//     margin: "6px 0 0",
//     fontSize: 13.5,
//     color: c.secondaryText,
//   }),
//   summaryCard: (c) => ({
//     padding: 20,
//     borderRadius: 16,
//     background: c.raisedBg,
//     border: `1px solid ${c.border}`,
//     position: "sticky",
//     top: 84,
//   }),
//   divider: (c) => ({
//     height: 1,
//     background: c.separator || c.border,
//     margin: "20px 0",
//   }),
//   addressGrid: {
//     display: "grid",
//     gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
//     gap: 14,
//   },
//   summaryRow: (c) => ({
//     display: "flex",
//     justifyContent: "space-between",
//     alignItems: "center",
//     fontSize: 13.5,
//     color: c.secondaryText,
//     padding: "7px 0",
//   }),
//   summaryTotal: (c) => ({
//     display: "flex",
//     justifyContent: "space-between",
//     alignItems: "center",
//     fontSize: 17,
//     fontWeight: 800,
//     color: c.heading,
//     letterSpacing: "-0.01em",
//     marginTop: 8,
//     paddingTop: 12,
//     borderTop: `1px solid ${c.separator || c.border}`,
//   }),
//   shippingList: (c) => ({
//     marginTop: 2,
//     marginBottom: 8,
//     paddingLeft: 10,
//     borderLeft: `2px solid ${c.separator || c.border}`,
//     display: "flex",
//     flexDirection: "column",
//     gap: 4,
//   }),
//   shippingLine: (c) => ({
//     display: "flex",
//     justifyContent: "space-between",
//     alignItems: "center",
//     gap: 8,
//     fontSize: 12,
//     color: c.secondaryText,
//   }),
//   payBtn: (c, enabled) => ({
//     width: "100%",
//     marginTop: 16,
//     padding: "13px 18px",
//     borderRadius: 12,
//     border: "none",
//     background: enabled ? c.btnBg : c.subtleBg || c.hoverBg,
//     color: enabled ? c.btnText : c.secondaryText,
//     fontSize: 14.5,
//     fontWeight: 700,
//     cursor: enabled ? "pointer" : "not-allowed",
//     display: "inline-flex",
//     alignItems: "center",
//     justifyContent: "center",
//     gap: 8,
//     transition:
//       "transform 0.15s ease, box-shadow 0.15s ease, background 0.15s ease",
//     boxShadow: enabled ? `0 4px 14px ${c.btnBg}33` : "none",
//   }),
//   reassure: (c) => ({
//     marginTop: 12,
//     display: "flex",
//     alignItems: "center",
//     justifyContent: "center",
//     gap: 6,
//     fontSize: 11.5,
//     color: c.secondaryText,
//   }),
//   removeBtn: (c) => ({
//     width: 30,
//     height: 30,
//     borderRadius: 8,
//     border: "none",
//     background: "transparent",
//     color: c.secondaryText,
//     cursor: "pointer",
//     display: "inline-flex",
//     alignItems: "center",
//     justifyContent: "center",
//     flexShrink: 0,
//     transition: "background 0.15s ease, color 0.15s ease",
//   }),
//   qtyGroup: (c) => ({
//     display: "inline-flex",
//     alignItems: "center",
//     border: `1px solid ${c.border}`,
//     borderRadius: 10,
//     background: c.raisedBg,
//     overflow: "hidden",
//   }),
//   qtyBtn: (c, disabled) => ({
//     width: 32,
//     height: 32,
//     border: "none",
//     background: "transparent",
//     color: disabled ? c.secondaryText : c.text,
//     cursor: disabled ? "not-allowed" : "pointer",
//     display: "inline-flex",
//     alignItems: "center",
//     justifyContent: "center",
//     opacity: disabled ? 0.45 : 1,
//     transition: "background 0.15s ease",
//   }),
//   paymentBtn: (c, active) => ({
//     display: "flex",
//     alignItems: "center",
//     gap: 12,
//     width: "100%",
//     padding: 12,
//     borderRadius: 12,
//     border: `1.5px solid ${active ? c.btnBg : c.border}`,
//     background: active ? c.hoverBg : "transparent",
//     cursor: "pointer",
//     transition: "border-color 0.15s ease, background 0.15s ease",
//   }),
//   paymentIcon: (c, active) => ({
//     width: 32,
//     height: 32,
//     borderRadius: 9,
//     background: active ? c.btnBg : c.subtleBg || c.hoverBg,
//     color: active ? c.btnText : c.btnBg,
//     display: "inline-flex",
//     alignItems: "center",
//     justifyContent: "center",
//     flexShrink: 0,
//     transition: "background 0.15s ease, color 0.15s ease",
//   }),
//   radio: (c, active) => ({
//     width: 18,
//     height: 18,
//     borderRadius: "50%",
//     border: `1.5px solid ${active ? c.btnBg : c.border}`,
//     display: "inline-flex",
//     alignItems: "center",
//     justifyContent: "center",
//     flexShrink: 0,
//     transition: "border-color 0.15s ease",
//   }),
//   radioDot: (c) => ({
//     width: 8,
//     height: 8,
//     borderRadius: "50%",
//     background: c.btnBg,
//   }),
//   emptyCta: (c, primary) => ({
//     display: "inline-flex",
//     alignItems: "center",
//     gap: 8,
//     padding: "11px 18px",
//     borderRadius: 11,
//     fontWeight: 700,
//     fontSize: 13.5,
//     textDecoration: "none",
//     background: primary ? c.btnBg : "transparent",
//     color: primary ? c.btnText : c.text,
//     border: primary ? "none" : `1px solid ${c.border}`,
//   }),
//   loadingWrap: (c) => ({
//     display: "flex",
//     flexDirection: "column",
//     alignItems: "center",
//     justifyContent: "center",
//     minHeight: 320,
//     gap: 14,
//     color: c.secondaryText,
//   }),
//   spinner: (c) => ({
//     width: 34,
//     height: 34,
//     border: `3px solid ${c.border}`,
//     borderTopColor: c.btnBg,
//     borderRadius: "50%",
//     animation: "spin 0.9s linear infinite",
//   }),
//   spinnerInline: (c) => ({
//     width: 14,
//     height: 14,
//     border: `2px solid ${c.btnText}55`,
//     borderTopColor: c.btnText,
//     borderRadius: "50%",
//     animation: "spin 0.8s linear infinite",
//   }),
// };

// /* Responsive + keyframes */
// if (
//   typeof window !== "undefined" &&
//   !document.getElementById("cart-responsive-style")
// ) {
//   const styleEl = document.createElement("style");
//   styleEl.id = "cart-responsive-style";
//   styleEl.textContent = `
//     @keyframes spin { to { transform: rotate(360deg); } }
//     @media (min-width: 900px) {
//       .cart-layout-responsive {
//         grid-template-columns: minmax(0, 1.6fr) minmax(320px, 1fr);
//       }
//     }
//     @media (max-width: 480px) {
//       .cart-address-grid {
//         grid-template-columns: 1fr !important;
//       }
//       .cart-address-grid > * {
//         grid-column: span 1 !important;
//       }
//     }
//   `;
//   document.head.appendChild(styleEl);
// }



























// screens/CartScreen.jsx
import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../../providers/AuthProvider";
import { useTheme } from "../../hooks/useTheme";
import { useToast } from "../../providers/ToastProvider";
import {
  ShoppingBag,
  Minus,
  Plus,
  Trash2,
  Truck,
  Clock,
  ShieldCheck,
  CreditCard,
  Wallet,
  AlertCircle,
  ArrowRight,
  Package,
  MapPin,
  Layers,
  Boxes,
  Info,
} from "lucide-react";
import { useCart } from "../../contexts/CartContext";
import { variantKeyLabel } from "../marketplace/pricingApi";
import { isWholesale } from "../../utils/variants";

const PAYMENT_METHODS = [
  {
    value: "PAYSTACK",
    icon: CreditCard,
    title: "Card or Mobile Money",
    subtitle: "Secured by Paystack",
  },
  {
    value: "WALLET",
    icon: Wallet,
    title: "Azoemart Wallet",
    subtitle: "Instant checkout from balance",
  },
];

const ADDRESS_FIELDS = [
  { key: "full_name", label: "Full name", placeholder: "Kwame Mensah", span: 2 },
  { key: "phone", label: "Phone number", placeholder: "024 000 0000", type: "tel" },
  { key: "city", label: "City / Town", placeholder: "Accra" },
  { key: "region", label: "Region", placeholder: "Greater Accra" },
  {
    key: "address_line",
    label: "Delivery address",
    placeholder: "Hostel, block, room number",
    span: 2,
  },
];

const money = (v) => `GH₵${Number(v || 0).toFixed(2)}`;

const readError = (data, fallback = "Something went wrong") => {
  if (!data) return fallback;
  if (data.message) return data.message;
  const errors = data.errors;
  if (errors) {
    const flat = Object.values(errors).flat().filter(Boolean);
    if (flat.length) return flat.join(" ");
  }
  return fallback;
};

export default function CartScreen() {
  const { authFetch } = useAuth();
  const { colors } = useTheme();
  const { showToast } = useToast();
  const { refresh: refreshCart, clear: clearCart } = useCart();

  const [cart, setCart] = useState(null);
  const [loading, setLoading] = useState(true);
  const [placing, setPlacing] = useState(false);
  const [busyItems, setBusyItems] = useState(() => new Set());
  const [paymentMethod, setPaymentMethod] = useState("PAYSTACK");
  const [address, setAddress] = useState({
    full_name: "",
    phone: "",
    address_line: "",
    city: "",
    region: "",
  });
  const [touched, setTouched] = useState({});

  const load = useCallback(async () => {
    try {
      const res = await authFetch("/marketplace/cart/");
      const data = await res.json();
      if (res.ok) setCart(data.data);
      else showToast(readError(data, "Could not load cart"), "error");
    } catch {
      showToast("Network error loading cart", "error");
    } finally {
      setLoading(false);
    }
  }, [authFetch, showToast]);

  useEffect(() => {
    load();
  }, [load]);

  const items = cart?.items ?? [];
  const isEmpty = !loading && items.length === 0;

  const cartComposition = useMemo(() => {
    const types = new Set(items.map((i) => i.item_type));
    return {
      hasReady: types.has("READY") || types.has("INSTANT"),
      hasPreOrder: types.has("PRE_ORDER"),
      isMixed: types.size > 1,
    };
  }, [items]);

  const belowMoqItems = useMemo(
    () =>
      items.filter(
        (i) => isWholesale(i) && Number(i.quantity) < Number(i.moq)
      ),
    [items]
  );

  const subtotal = useMemo(
    () =>
      items.reduce(
        (sum, i) => sum + Number(i.price) * Number(i.quantity),
        0
      ),
    [items]
  );

  const addressErrors = useMemo(() => {
    const errs = {};
    if (!address.full_name.trim()) errs.full_name = "Name is required";
    if (!address.phone.trim()) errs.phone = "Phone is required";
    else if (address.phone.replace(/\D/g, "").length < 9)
      errs.phone = "Enter a valid phone number";
    if (!address.address_line.trim()) errs.address_line = "Address is required";
    if (!address.city.trim()) errs.city = "City is required";
    return errs;
  }, [address]);

  const canCheckout =
    !placing &&
    items.length > 0 &&
    !cartComposition.isMixed &&
    belowMoqItems.length === 0 &&
    Object.keys(addressErrors).length === 0;

  const setItemBusy = useCallback((id, busy) => {
    setBusyItems((prev) => {
      const next = new Set(prev);
      if (busy) next.add(id);
      else next.delete(id);
      return next;
    });
  }, []);

  const recomputeCart = (nextItems) => {
    if (!cart) return null;
    const total = nextItems.reduce(
      (sum, i) => sum + Number(i.price) * Number(i.quantity),
      0
    );
    return {
      ...cart,
      items: nextItems,
      total_price: total,
      total_items: nextItems.reduce((s, i) => s + Number(i.quantity), 0),
    };
  };

  const changeQuantity = async (item, nextQty) => {
    if (busyItems.has(item.id)) return;
    if (isWholesale(item) && nextQty < Number(item.moq)) {
      showToast(
        `Minimum order for ${item.product_name} is ${item.moq} units`,
        "error"
      );
      return;
    }
    if (nextQty < 1) return;

    const prevCart = cart;
    const nextItems = cart.items.map((i) =>
      i.id === item.id ? { ...i, quantity: nextQty } : i
    );

    setCart(recomputeCart(nextItems));
    setItemBusy(item.id, true);

    try {
      const res = await authFetch(`/marketplace/cart/items/${item.id}/`, {
        method: "PATCH",
        body: JSON.stringify({ quantity: nextQty }),
      });
      const data = await res.json();

      if (!res.ok) {
        setCart(prevCart);
        showToast(readError(data, "Could not update quantity"), "error");
        return;
      }

      if (data.data?.items && Array.isArray(data.data.items)) {
        setCart(data.data);
      } else if (data.data?.quantity != null) {
        setCart((c) => {
          if (!c) return c;
          const reconciled = c.items.map((i) =>
            i.id === item.id ? { ...i, quantity: Number(data.data.quantity) } : i
          );
          return recomputeCart(reconciled);
        });
      }
      refreshCart();
    } catch {
      setCart(prevCart);
      showToast("Network error", "error");
    } finally {
      setItemBusy(item.id, false);
    }
  };

  const removeItem = async (item) => {
    if (busyItems.has(item.id)) return;
    const prevCart = cart;
    const nextItems = cart.items.filter((i) => i.id !== item.id);
    setCart(recomputeCart(nextItems));
    setItemBusy(item.id, true);
    try {
      const res = await authFetch(`/marketplace/cart/items/${item.id}/`, {
        method: "DELETE",
      });
      if (!res.ok) {
        setCart(prevCart);
        const data = await res.json().catch(() => ({}));
        showToast(readError(data, "Could not remove item"), "error");
      } else {
        refreshCart();
      }
    } catch {
      setCart(prevCart);
      showToast("Network error", "error");
    } finally {
      setItemBusy(item.id, false);
    }
  };

  const checkout = async () => {
    setTouched(Object.fromEntries(Object.keys(address).map((k) => [k, true])));

    if (Object.keys(addressErrors).length > 0) {
      showToast("Please complete the delivery details", "error");
      return;
    }
    if (cartComposition.isMixed) {
      showToast(
        "Ready-to-ship and pre-order items must be checked out separately",
        "error"
      );
      return;
    }
    if (belowMoqItems.length > 0) {
      const first = belowMoqItems[0];
      showToast(
        `Minimum order for ${first.product_name} is ${first.moq} units`,
        "error"
      );
      return;
    }

    setPlacing(true);
    try {
      const orderRes = await authFetch("/marketplace/orders/", {
        method: "POST",
        body: JSON.stringify({
          items: items.map((i) => ({
            product_id: i.product,
            quantity: i.quantity,
            ...(i.variant_key && { variant_key: i.variant_key }),
            ...(i.pre_order && { pre_order_id: i.pre_order }),
          })),
          shipping_address: address,
          shipping_method: "standard",
        }),
      });
      const orderData = await orderRes.json();
      if (!orderRes.ok) {
        throw new Error(readError(orderData, "Could not create order"));
      }

      const payRes = await authFetch(
        `/marketplace/orders/${orderData.data.id}/pay/`,
        {
          method: "POST",
          body: JSON.stringify({ payment_method: paymentMethod }),
        }
      );
      const payData = await payRes.json();
      if (!payRes.ok) {
        throw new Error(readError(payData, "Payment could not be started"));
      }

      await authFetch("/marketplace/cart/clear/", { method: "DELETE" });
      clearCart();
      if (paymentMethod === "PAYSTACK" && payData.data?.authorization_url) {
        window.location.assign(payData.data.authorization_url);
        return;
      }

      showToast("Order paid from your wallet", "success");
      await load();
    } catch (e) {
      showToast(e.message || "Checkout failed", "error");
    } finally {
      setPlacing(false);
    }
  };

  if (loading) {
    return (
      <div style={styles.loadingWrap(colors)}>
        <div style={styles.spinner(colors)} />
        <span style={{ fontSize: 13, color: colors.secondaryText }}>
          Loading your cart…
        </span>
      </div>
    );
  }

  if (isEmpty) {
    return (
      <div style={styles.pageWrap}>
        <EmptyCart colors={colors} />
      </div>
    );
  }

  return (
    <div style={styles.pageWrap}>
      <header style={{ marginBottom: 24 }}>
        <h1 style={styles.h1(colors)}>Your cart</h1>
        <p style={styles.subtitle(colors)}>
          {items.length} item{items.length !== 1 ? "s" : ""} ready for checkout
        </p>
      </header>

      <div style={styles.layout} className="cart-layout-responsive">
        <div style={styles.leftCol}>
          <Section
            colors={colors}
            title="Items"
            icon={ShoppingBag}
            count={items.length}
          >
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {items.map((item) => (
                <CartRow
                  key={item.id}
                  item={item}
                  colors={colors}
                  busy={busyItems.has(item.id)}
                  onChangeQty={(q) => changeQuantity(item, q)}
                  onRemove={() => removeItem(item)}
                />
              ))}
            </div>

            {cartComposition.isMixed && (
              <InlineWarning colors={colors} tone="warning">
                Your cart mixes ready-to-ship and pre-order items. These must
                be checked out in separate orders. Remove one type to continue.
              </InlineWarning>
            )}

            {belowMoqItems.length > 0 && (
              <InlineWarning colors={colors} tone="error">
                {belowMoqItems.length === 1 ? (
                  <>
                    <strong>{belowMoqItems[0].product_name}</strong> requires a
                    minimum order of {belowMoqItems[0].moq} units. Increase the
                    quantity to continue.
                  </>
                ) : (
                  <>
                    {belowMoqItems.length} wholesale items are below their
                    minimum order. Increase each to its MOQ to continue.
                  </>
                )}
              </InlineWarning>
            )}
          </Section>

          <Section colors={colors} title="Delivery details" icon={MapPin}>
            <div style={styles.addressGrid} className="cart-address-grid">
              {ADDRESS_FIELDS.map((f) => (
                <AddressField
                  key={f.key}
                  field={f}
                  value={address[f.key]}
                  error={touched[f.key] ? addressErrors[f.key] : null}
                  onChange={(v) => setAddress((a) => ({ ...a, [f.key]: v }))}
                  onBlur={() =>
                    setTouched((t) => ({ ...t, [f.key]: true }))
                  }
                  colors={colors}
                />
              ))}
            </div>
          </Section>
        </div>

        <aside style={styles.rightCol}>
          <div style={styles.summaryCard(colors)}>
            <Section colors={colors} title="Payment" icon={CreditCard} bare>
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {PAYMENT_METHODS.map((m) => {
                  const Icon = m.icon;
                  const active = paymentMethod === m.value;
                  return (
                    <button
                      key={m.value}
                      type="button"
                      onClick={() => setPaymentMethod(m.value)}
                      style={styles.paymentBtn(colors, active)}
                    >
                      <span style={styles.paymentIcon(colors, active)} aria-hidden>
                        <Icon size={16} />
                      </span>
                      <span style={{ flex: 1, textAlign: "left" }}>
                        <span
                          style={{
                            display: "block",
                            fontSize: 13.5,
                            fontWeight: 700,
                            color: colors.heading,
                          }}
                        >
                          {m.title}
                        </span>
                        <span
                          style={{
                            display: "block",
                            fontSize: 11.5,
                            color: colors.secondaryText,
                            marginTop: 2,
                          }}
                        >
                          {m.subtitle}
                        </span>
                      </span>
                      <span style={styles.radio(colors, active)}>
                        {active && <span style={styles.radioDot(colors)} />}
                      </span>
                    </button>
                  );
                })}
              </div>
            </Section>

            <div style={styles.divider(colors)} />

            <Summary colors={colors} subtotal={subtotal} total={subtotal} />

            <button
              type="button"
              disabled={!canCheckout}
              onClick={checkout}
              style={styles.payBtn(colors, canCheckout)}
            >
              {placing ? (
                <>
                  <span style={styles.spinnerInline(colors)} />
                  Processing…
                </>
              ) : (
                <>
                  Place order · {money(subtotal)}
                  <ArrowRight size={16} />
                </>
              )}
            </button>

            <div style={styles.reassure(colors)}>
              <ShieldCheck size={13} />
              Secure checkout · Your details are encrypted
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════
   Sub-components
   ══════════════════════════════════════════════════════════ */

const Section = ({ colors, title, icon: Icon, count, children, bare }) => (
  <section style={{ marginBottom: bare ? 0 : 28 }}>
    {title && (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          marginBottom: 14,
        }}
      >
        {Icon && (
          <span
            style={{
              width: 28,
              height: 28,
              borderRadius: 8,
              background: colors.subtleBg || colors.hoverBg,
              color: colors.btnBg,
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <Icon size={14} />
          </span>
        )}
        <h2
          style={{
            margin: 0,
            fontSize: 15,
            fontWeight: 700,
            color: colors.heading,
            letterSpacing: "-0.01em",
          }}
        >
          {title}
          {typeof count === "number" && (
            <span
              style={{
                marginLeft: 8,
                fontSize: 12,
                fontWeight: 600,
                color: colors.secondaryText,
              }}
            >
              ({count})
            </span>
          )}
        </h2>
      </div>
    )}
    {children}
  </section>
);

const CartRow = ({ item, colors, busy, onChangeQty, onRemove }) => {
  const isPreOrder = item.item_type === "PRE_ORDER";
  const hasVariant = !!item.variant_key;
  const wholesale = isWholesale(item);
  const moqFloor = wholesale ? Number(item.moq) : 1;
  const belowMoq = wholesale && Number(item.quantity) < moqFloor;

  return (
    <div
      style={{
        display: "flex",
        gap: 14,
        padding: 14,
        borderRadius: 14,
        border: `1px solid ${belowMoq ? colors.error : colors.border}`,
        background: colors.raisedBg,
        opacity: busy ? 0.55 : 1,
        transition: "opacity 0.15s ease, border-color 0.15s ease",
        alignItems: "flex-start",
        pointerEvents: busy ? "none" : "auto",
      }}
    >
      <div
        style={{
          width: 76,
          height: 76,
          borderRadius: 10,
          overflow: "hidden",
          background: colors.subtleBg || colors.hoverBg,
          flexShrink: 0,
        }}
      >
        {item.product_image ? (
          <img
            src={item.product_image}
            alt=""
            style={{ width: "100%", height: "100%", objectFit: "cover" }}
          />
        ) : (
          <div
            style={{
              width: "100%",
              height: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: colors.secondaryText,
            }}
          >
            <Package size={22} />
          </div>
        )}
      </div>

      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: "flex", alignItems: "flex-start", gap: 8 }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div
              style={{
                fontSize: 14,
                fontWeight: 700,
                color: colors.heading,
                lineHeight: 1.35,
                display: "-webkit-box",
                WebkitLineClamp: 2,
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
              }}
            >
              {item.product_name}
            </div>

            {hasVariant && (
              <div
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 4,
                  marginTop: 4,
                  padding: "2px 8px",
                  borderRadius: 999,
                  background: `${colors.btnBg}18`,
                  color: colors.btnBg,
                  fontSize: 10.5,
                  fontWeight: 700,
                  letterSpacing: 0.2,
                }}
              >
                <Layers size={10} />
                {variantKeyLabel(item.variant_key)}
              </div>
            )}

            <div
              style={{
                marginTop: 6,
                display: "flex",
                alignItems: "center",
                gap: 6,
                flexWrap: "wrap",
              }}
            >
              <span
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 4,
                  fontSize: 10.5,
                  fontWeight: 700,
                  letterSpacing: 0.3,
                  textTransform: "uppercase",
                  padding: "2px 8px",
                  borderRadius: 999,
                  color: isPreOrder ? colors.warning : colors.success,
                  background: isPreOrder ? colors.warningBg : colors.successBg,
                }}
              >
                {isPreOrder ? <Clock size={10} /> : <Truck size={10} />}
                {item.item_type_display ||
                  (isPreOrder ? "Pre-order" : "Ready to ship")}
              </span>

              {wholesale && (
                <span
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 4,
                    fontSize: 10.5,
                    fontWeight: 700,
                    letterSpacing: 0.3,
                    textTransform: "uppercase",
                    padding: "2px 8px",
                    borderRadius: 999,
                    background: `${colors.btnBg}18`,
                    color: colors.btnBg,
                  }}
                >
                  <Boxes size={10} />
                  Wholesale · Min {item.moq}
                </span>
              )}
            </div>

            {belowMoq && (
              <div
                style={{
                  marginTop: 8,
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 6,
                  padding: "6px 10px",
                  borderRadius: 8,
                  background: colors.errorBg,
                  color: colors.error,
                  fontSize: 11.5,
                  fontWeight: 600,
                }}
              >
                <AlertCircle size={12} />
                Minimum is {moqFloor} units
              </div>
            )}
          </div>

          <button
            type="button"
            onClick={onRemove}
            disabled={busy}
            aria-label="Remove item"
            style={styles.removeBtn(colors)}
          >
            <Trash2 size={15} />
          </button>
        </div>

        <div
          style={{
            marginTop: 12,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 10,
            flexWrap: "wrap",
          }}
        >
          <div style={styles.qtyGroup(colors)}>
            <button
              type="button"
              onClick={() => onChangeQty(item.quantity - 1)}
              disabled={busy || item.quantity <= moqFloor}
              style={styles.qtyBtn(colors, busy || item.quantity <= moqFloor)}
              aria-label="Decrease quantity"
            >
              <Minus size={13} />
            </button>
            <span
              style={{
                minWidth: 26,
                textAlign: "center",
                fontSize: 13.5,
                fontWeight: 700,
                color: colors.heading,
              }}
            >
              {item.quantity}
            </span>
            <button
              type="button"
              onClick={() => onChangeQty(item.quantity + 1)}
              disabled={busy}
              style={styles.qtyBtn(colors, busy)}
              aria-label="Increase quantity"
            >
              <Plus size={13} />
            </button>
          </div>

          <div style={{ textAlign: "right" }}>
            <div
              style={{
                fontSize: 11,
                color: colors.secondaryText,
                fontWeight: 500,
              }}
            >
              {money(item.price)} each
            </div>
            <div
              style={{
                fontSize: 16,
                fontWeight: 800,
                color: colors.heading,
                letterSpacing: "-0.01em",
              }}
            >
              {money(Number(item.price) * item.quantity)}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const AddressField = ({ field, value, error, onChange, onBlur, colors }) => (
  <div style={{ gridColumn: `span ${field.span || 1}` }}>
    <label
      htmlFor={`addr-${field.key}`}
      style={{
        display: "block",
        fontSize: 12,
        fontWeight: 600,
        color: colors.text,
        marginBottom: 6,
      }}
    >
      {field.label}
    </label>
    <input
      id={`addr-${field.key}`}
      type={field.type || "text"}
      value={value}
      placeholder={field.placeholder}
      onChange={(e) => onChange(e.target.value)}
      onBlur={onBlur}
      style={{
        width: "100%",
        padding: "10px 12px",
        borderRadius: 10,
        border: `1px solid ${error ? colors.error : colors.border}`,
        background: colors.raisedBg,
        color: colors.text,
        fontSize: 14,
        outline: "none",
        boxSizing: "border-box",
        transition: "border-color 0.15s ease, box-shadow 0.15s ease",
        boxShadow: error ? `0 0 0 3px ${colors.errorBg}` : "none",
      }}
    />
    {error && (
      <div
        style={{
          marginTop: 4,
          fontSize: 11.5,
          color: colors.error,
          display: "flex",
          alignItems: "center",
          gap: 4,
        }}
      >
        <AlertCircle size={11} />
        {error}
      </div>
    )}
  </div>
);

/* ══════════════════════════════════════════════════════════
   Summary — subtotal + shipping note + total
   ══════════════════════════════════════════════════════════ */

const Summary = ({ colors, subtotal, total }) => (
  <div>
    <div style={styles.summaryRow(colors)}>
      <span>Subtotal</span>
      <span>{money(subtotal)}</span>
    </div>

    <div style={styles.shippingNote(colors)}>
      <Info size={13} style={{ flexShrink: 0, marginTop: 1 }} />
      <span>
        Shipping is charged <strong>per item</strong> when it arrives in Ghana.
        You'll be notified when it's ready to pay.
      </span>
    </div>

    <div style={styles.summaryTotal(colors)}>
      <span>Total today</span>
      <span>{money(total)}</span>
    </div>
  </div>
);

const InlineWarning = ({ children, colors, tone = "warning" }) => {
  const isError = tone === "error";
  const fg = isError ? colors.error : colors.warning;
  const bg = isError ? colors.errorBg : colors.warningBg;
  return (
    <div
      style={{
        marginTop: 12,
        padding: 12,
        borderRadius: 12,
        background: bg,
        border: `1px solid ${fg}33`,
        color: fg,
        fontSize: 12.5,
        fontWeight: 600,
        display: "flex",
        alignItems: "flex-start",
        gap: 8,
        lineHeight: 1.5,
      }}
    >
      <AlertCircle size={15} style={{ flexShrink: 0, marginTop: 1 }} />
      <span>{children}</span>
    </div>
  );
};

const EmptyCart = ({ colors }) => (
  <div
    style={{
      maxWidth: 400,
      margin: "80px auto 0",
      textAlign: "center",
      padding: 24,
    }}
  >
    <div
      style={{
        width: 76,
        height: 76,
        borderRadius: 22,
        background: colors.subtleBg || colors.hoverBg,
        color: colors.btnBg,
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        marginBottom: 18,
      }}
    >
      <ShoppingBag size={32} />
    </div>
    <h2
      style={{
        margin: "0 0 6px",
        fontSize: 20,
        fontWeight: 800,
        color: colors.heading,
        letterSpacing: "-0.02em",
      }}
    >
      Your cart is empty
    </h2>
    <p
      style={{
        margin: "0 0 24px",
        fontSize: 14,
        color: colors.secondaryText,
        lineHeight: 1.55,
      }}
    >
      Browse data bundles or marketplace products to get started.
    </p>
    <div
      style={{
        display: "flex",
        gap: 10,
        justifyContent: "center",
        flexWrap: "wrap",
      }}
    >
      <Link to="/marketplace" style={styles.emptyCta(colors, true)}>
        <ShoppingBag size={15} />
        Browse market
      </Link>
      <Link to="/data" style={styles.emptyCta(colors, false)}>
        <ArrowRight size={15} />
        Buy data
      </Link>
    </div>
  </div>
);

/* ══════════════════════════════════════════════════════════
   Styles
   ══════════════════════════════════════════════════════════ */

const styles = {
  pageWrap: {
    maxWidth: 1080,
    margin: "0 auto",
    padding: "20px 16px 60px",
  },
  layout: {
    display: "grid",
    gridTemplateColumns: "minmax(0, 1fr)",
    gap: 24,
  },
  leftCol: { minWidth: 0 },
  rightCol: { minWidth: 0 },
  h1: (c) => ({
    margin: 0,
    fontSize: 26,
    fontWeight: 800,
    letterSpacing: "-0.02em",
    color: c.heading,
  }),
  subtitle: (c) => ({
    margin: "6px 0 0",
    fontSize: 13.5,
    color: c.secondaryText,
  }),
  summaryCard: (c) => ({
    padding: 20,
    borderRadius: 16,
    background: c.raisedBg,
    border: `1px solid ${c.border}`,
    position: "sticky",
    top: 84,
  }),
  divider: (c) => ({
    height: 1,
    background: c.separator || c.border,
    margin: "20px 0",
  }),
  addressGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
    gap: 14,
  },
  summaryRow: (c) => ({
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    fontSize: 13.5,
    color: c.secondaryText,
    padding: "7px 0",
  }),
  shippingNote: (c) => ({
    display: "flex",
    alignItems: "flex-start",
    gap: 8,
    padding: "10px 12px",
    borderRadius: 10,
    background: c.subtleBg || c.hoverBg,
    border: `1px solid ${c.border}`,
    color: c.secondaryText,
    fontSize: 12,
    lineHeight: 1.5,
    fontWeight: 500,
    marginTop: 4,
    marginBottom: 12,
  }),
  summaryTotal: (c) => ({
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    fontSize: 17,
    fontWeight: 800,
    color: c.heading,
    letterSpacing: "-0.01em",
    marginTop: 8,
    paddingTop: 12,
    borderTop: `1px solid ${c.separator || c.border}`,
  }),
  payBtn: (c, enabled) => ({
    width: "100%",
    marginTop: 16,
    padding: "13px 18px",
    borderRadius: 12,
    border: "none",
    background: enabled ? c.btnBg : c.subtleBg || c.hoverBg,
    color: enabled ? c.btnText : c.secondaryText,
    fontSize: 14.5,
    fontWeight: 700,
    cursor: enabled ? "pointer" : "not-allowed",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 8,
    transition:
      "transform 0.15s ease, box-shadow 0.15s ease, background 0.15s ease",
    boxShadow: enabled ? `0 4px 14px ${c.btnBg}33` : "none",
  }),
  reassure: (c) => ({
    marginTop: 12,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 6,
    fontSize: 11.5,
    color: c.secondaryText,
  }),
  removeBtn: (c) => ({
    width: 30,
    height: 30,
    borderRadius: 8,
    border: "none",
    background: "transparent",
    color: c.secondaryText,
    cursor: "pointer",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
    transition: "background 0.15s ease, color 0.15s ease",
  }),
  qtyGroup: (c) => ({
    display: "inline-flex",
    alignItems: "center",
    border: `1px solid ${c.border}`,
    borderRadius: 10,
    background: c.raisedBg,
    overflow: "hidden",
  }),
  qtyBtn: (c, disabled) => ({
    width: 32,
    height: 32,
    border: "none",
    background: "transparent",
    color: disabled ? c.secondaryText : c.text,
    cursor: disabled ? "not-allowed" : "pointer",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    opacity: disabled ? 0.45 : 1,
    transition: "background 0.15s ease",
  }),
  paymentBtn: (c, active) => ({
    display: "flex",
    alignItems: "center",
    gap: 12,
    width: "100%",
    padding: 12,
    borderRadius: 12,
    border: `1.5px solid ${active ? c.btnBg : c.border}`,
    background: active ? c.hoverBg : "transparent",
    cursor: "pointer",
    transition: "border-color 0.15s ease, background 0.15s ease",
  }),
  paymentIcon: (c, active) => ({
    width: 32,
    height: 32,
    borderRadius: 9,
    background: active ? c.btnBg : c.subtleBg || c.hoverBg,
    color: active ? c.btnText : c.btnBg,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
    transition: "background 0.15s ease, color 0.15s ease",
  }),
  radio: (c, active) => ({
    width: 18,
    height: 18,
    borderRadius: "50%",
    border: `1.5px solid ${active ? c.btnBg : c.border}`,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
    transition: "border-color 0.15s ease",
  }),
  radioDot: (c) => ({
    width: 8,
    height: 8,
    borderRadius: "50%",
    background: c.btnBg,
  }),
  emptyCta: (c, primary) => ({
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    padding: "11px 18px",
    borderRadius: 11,
    fontWeight: 700,
    fontSize: 13.5,
    textDecoration: "none",
    background: primary ? c.btnBg : "transparent",
    color: primary ? c.btnText : c.text,
    border: primary ? "none" : `1px solid ${c.border}`,
  }),
  loadingWrap: (c) => ({
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    minHeight: 320,
    gap: 14,
    color: c.secondaryText,
  }),
  spinner: (c) => ({
    width: 34,
    height: 34,
    border: `3px solid ${c.border}`,
    borderTopColor: c.btnBg,
    borderRadius: "50%",
    animation: "spin 0.9s linear infinite",
  }),
  spinnerInline: (c) => ({
    width: 14,
    height: 14,
    border: `2px solid ${c.btnText}55`,
    borderTopColor: c.btnText,
    borderRadius: "50%",
    animation: "spin 0.8s linear infinite",
  }),
};

if (
  typeof window !== "undefined" &&
  !document.getElementById("cart-responsive-style")
) {
  const styleEl = document.createElement("style");
  styleEl.id = "cart-responsive-style";
  styleEl.textContent = `
    @keyframes spin { to { transform: rotate(360deg); } }
    @media (min-width: 900px) {
      .cart-layout-responsive {
        grid-template-columns: minmax(0, 1.6fr) minmax(320px, 1fr);
      }
    }
    @media (max-width: 480px) {
      .cart-address-grid {
        grid-template-columns: 1fr !important;
      }
      .cart-address-grid > * {
        grid-column: span 1 !important;
      }
    }
  `;
  document.head.appendChild(styleEl);
}